/**************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2022 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/

import { useCallback, useEffect, useState } from 'react';
import { useConfigContext } from '../ConfigContext/ConfigContext';

export const usePreviewDate = () => {
    const [configs] = useConfigContext();
    const [state, setState] = useState({});

    const applyPreviewDate = useCallback(
        previewDate =>
            Promise.all(
                configs.map(config => {
                    // Configure Preview-Version headers
                    if (previewDate !== null) {
                        // previewDate may be undefined, set it from config.previewDate
                        previewDate = previewDate || config.previewDate;
                    }

                    if (previewDate) {
                        // set
                        const timestamp = Math.floor(Date.parse(previewDate) / 1000);
                        config.gqlClient.setLinkWithHeadersAndMethod([['Preview-Version', timestamp]], 'POST');
                    } else {
                        // unset
                        config.gqlClient.setLinkWithHeadersAndMethod([['Preview-Version']], config.method);
                    }

                    // Clear cache
                    return config.gqlClient.resetStore();
                })
            ),
        [configs]
    );

    const setPreviewDate = async newPreviewDate => {
        // otherwise only apply the new preview date if it is different from the previous one
        if (state.previewDate !== newPreviewDate) {
            await applyPreviewDate(newPreviewDate);
            setState({ previewDate: newPreviewDate });
        }
    };

    const restorePreviewDate = async () => {
        await applyPreviewDate();
        setState({});
    };

    // update the previewDate if the config changes
    useEffect(() => {
        // only if a previewDate was set, apply it on the new config
        // we don't need to set the preview-version header when the config changes as that will anyway
        // initialise a new gql client and http link
        if (typeof state.previewDate !== 'undefined') {
            applyPreviewDate(state.previewDate);
        }
    }, [configs]);

    return [
        state,
        {
            setPreviewDate,
            restorePreviewDate
        }
    ];
};
